import { useMemo } from 'react'
import { any, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	createChoices,
	isFilterbaleField,
	isPreproField,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'
import { useBrandsQuery, useCategoriesQuery } from '@/entities/core'
import {
	useOptimizationGroupsQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'

import { HiddenOnContext } from '@/shared/generated'

export const useFiltersParams = (optimizationGroupId?: string) => {
	const { t } = useTranslation()

	const fieldsQuery = useColumnsSchemaQuery(HiddenOnContext.Assortment, {
		select: (data) => data.filter(isPreproField).filter(isFilterbaleField),
	})
	const optimizationGroupQuery = useOptimizationGroupsQuery()
	const salesEntityQuery = useSalesEntitiesQuery()
	const brandsQuery = useBrandsQuery(
		optimizationGroupId ? { optimization_group_id: optimizationGroupId } : {}
	)
	const categoriesQuery = useCategoriesQuery({
		optimization_group_id: optimizationGroupId!,
	})
	const fields = useMemo(
		() => fieldsQuery.data?.map((schema) => toAdaptedSchema(schema, t)) || [],
		[fieldsQuery.data, t]
	)

	const entities = useMemo(
		() => ({
			sales_level: (salesEntityQuery.data?.sales_levels || []).map((e) => ({
				...e,
				id: e.level,
			})),
			sales_entity: salesEntityQuery.data?.sales_entities || [],
			category: categoriesQuery.data || [],
			brand: brandsQuery.data || [],
			optimization_group: optimizationGroupQuery.data || [],
		}),
		[
			categoriesQuery.data,
			brandsQuery.data,
			optimizationGroupQuery.data,
			salesEntityQuery.data,
		]
	)
	const dataChoices = useMemo(() => {
		const schema = fieldsQuery.data || []

		return createChoices({
			schema,
			entities,
		})
	}, [entities, fieldsQuery.data])
	const isLoading = any(prop('isLoading'), [
		fieldsQuery,
		categoriesQuery,
		brandsQuery,
		optimizationGroupQuery,
		salesEntityQuery,
	])

	return useMemo(
		() => ({
			fields,
			dataChoices,
			isLoading,
		}),
		[fields, dataChoices, isLoading]
	)
}
